'use client';

import { c as _c } from "react/compiler-runtime";
import { type GetThesisRow, useAddThesisReply, useGetThesis } from '@morfi/backend-sdk-v2/client';
import { useLocale, useTranslations } from 'next-intl';
import type React from 'react';
import { useRef, useState } from 'react';
import { UserAvatar } from '@/components/images/user-avatar';
import { UserIdentity } from '@/components/profile/user-identity';
import type { ThesisReplyWriteAccess } from '@/components/social/debate/thesis-reply-write-access';
import { useThesisReplyAccess } from '@/components/social/debate/use-thesis-reply-access';
import { AnimatedButton } from '@/components/ui/animated-button';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { Icon } from '@/components/ui/icon';
import { Delete02Icon } from '@/components/ui/icon/glyphs';
import { Panel, PanelContent } from '@/components/ui/panel';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { TextareaField } from '@/components/ui/textarea-field';
import { TOAST_IDS } from '@/config/toast-ids';
import { onEvent } from '@/events';
import { useMediaQuery } from '@/hooks/ui/use-media-query';
import { useAuthGate } from '@/hooks/use-auth-gate';
import { Link } from '@/i18n/navigation';
import { useFormattedCurrency } from '@/lib/currency';
import { formatRelativeTime } from '@/lib/format-relative-time';
import { logger } from '@/lib/telemetry/logger';
import { showToast } from '@/lib/ui/toast';
import { cn } from '@/lib/ui/utils';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_REPLY_LENGTH = 280;
/** Counter appears once the user reaches 80% of the cap (spec 094 FR-028). */
const REPLY_COUNTER_THRESHOLD: number = Math.floor(0.8 * MAX_REPLY_LENGTH);
const RATE_LIMIT_WINDOW_MS = 60_000;
const RATE_LIMIT_MAX = 5;
type ThesisReply = GetThesisRow['replies'][number];

/** Stable formatter for `<AnimatedNumber>` (effect depends on `format` identity). */
function formatReplyCharCount(value: number): string {
  return String(Math.round(value));
}
const REPLY_CHAR_COUNT_ANIMATION_MS = 250 as const;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ThesisReplyThreadProps {
  thesisSignature: string;
  marketPubkey: string;
  isAuthenticated: boolean;
  userId: string | null;
  writeAccess: ThesisReplyWriteAccess;
  onClose?: () => void;
  onOpenTradeForm?: () => void;
  onReplyAdded?: () => void;
  onReplyRemoved?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ThesisReplyThread(t0) {
  const $ = _c(73);
  const {
    thesisSignature,
    marketPubkey,
    isAuthenticated,
    userId,
    writeAccess,
    onClose,
    onOpenTradeForm,
    onReplyAdded,
    onReplyRemoved
  } = t0;
  const locale = useLocale();
  const t = useTranslations("social.debate");
  const tTimeIntl = useTranslations("time");
  let t1;
  if ($[0] !== tTimeIntl) {
    t1 = (key, params) => tTimeIntl(key as Parameters<typeof tTimeIntl>[0], params);
    $[0] = tTimeIntl;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const tTime = t1;
  const isMobile = useMediaQuery("(max-width: 767px)");
  const {
    isLoading: isAuthLoading,
    openLogin
  } = useAuthGate();
  let t2;
  if ($[2] !== isMobile || $[3] !== onClose || $[4] !== onOpenTradeForm) {
    t2 = isMobile && onOpenTradeForm !== undefined ? () => {
      onClose?.();
      onOpenTradeForm();
    } : undefined;
    $[2] = isMobile;
    $[3] = onClose;
    $[4] = onOpenTradeForm;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const openTradeForm = t2;
  let t3;
  if ($[6] !== openTradeForm) {
    t3 = openTradeForm !== undefined ? {
      openTradeForm
    } : {};
    $[6] = openTradeForm;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== isAuthenticated || $[9] !== isMobile || $[10] !== marketPubkey || $[11] !== t3 || $[12] !== thesisSignature || $[13] !== writeAccess) {
    t4 = {
      thesisSignature,
      marketPubkey,
      isAuthenticated,
      isMobile,
      writeAccess,
      ...t3
    };
    $[8] = isAuthenticated;
    $[9] = isMobile;
    $[10] = marketPubkey;
    $[11] = t3;
    $[12] = thesisSignature;
    $[13] = writeAccess;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const replyAccess = useThesisReplyAccess(t4);
  let t5;
  if ($[15] !== thesisSignature) {
    t5 = {
      signature: thesisSignature
    };
    $[15] = thesisSignature;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const thesisQuery = useGetThesis(t5);
  const addReplyMutation = useAddThesisReply();
  let t6;
  if ($[17] !== thesisQuery.data?.replies) {
    t6 = thesisQuery.data?.replies ?? [];
    $[17] = thesisQuery.data?.replies;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const replies = t6;
  let t7;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = new Set();
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const [deletedIds, setDeletedIds] = useState(t7);
  const [replyText, setReplyText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  let t8;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = [];
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const replyTimestampsRef = useRef(t8);
  let t9;
  if ($[21] !== deletedIds || $[22] !== replies) {
    let t10;
    if ($[24] !== deletedIds) {
      t10 = r => !deletedIds.has(r.id);
      $[24] = deletedIds;
      $[25] = t10;
    } else {
      t10 = $[25];
    }
    t9 = replies.filter(t10);
    $[21] = deletedIds;
    $[22] = replies;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const visibleReplies = t9;
  let t10;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = function isRateLimited() {
      const now = Date.now();
      const recentTimestamps = replyTimestampsRef.current.filter(ts => now - ts < RATE_LIMIT_WINDOW_MS);
      replyTimestampsRef.current = recentTimestamps;
      return recentTimestamps.length >= RATE_LIMIT_MAX;
    };
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const isRateLimited = t10;
  let t11;
  if ($[27] !== addReplyMutation || $[28] !== onReplyAdded || $[29] !== replyAccess || $[30] !== replyText || $[31] !== submitting || $[32] !== t || $[33] !== thesisSignature) {
    t11 = function handleSubmitReply() {
      if (replyText.trim().length === 0 || replyText.length > MAX_REPLY_LENGTH) {
        return;
      }
      if (isRateLimited()) {
        return;
      }
      if (submitting) {
        return;
      }
      replyTimestampsRef.current = [...replyTimestampsRef.current, Date.now()];
      setSubmitting(true);
      const content = replyText.trim();
      setReplyText("");
      addReplyMutation.mutateAsync({
        signature: thesisSignature,
        content
      }).then(() => {
        onReplyAdded?.();
        onEvent("social_thesis_replied", {
          signature: thesisSignature
        });
        showToast({
          id: TOAST_IDS.THESIS_REPLY_SUBMITTED,
          message: t("reply_submitted"),
          variant: "success"
        });
      }).catch(err => {
        const sdkError = err as {
          apiCode?: unknown;
          backendCode?: unknown;
        };
        const backendCode = sdkError.apiCode ?? sdkError.backendCode;
        const isGateFailure = backendCode === "THESIS_REPLY_REQUIRES_POSITION";
        if (isGateFailure) {
          setReplyText(content);
          replyAccess.rejectAccess();
        } else {
          logger.error("[ThesisReplyThread] submit reply failed", {
            error: err
          });
        }
        showToast({
          id: TOAST_IDS.THESIS_REPLY_SUBMITTED,
          message: isGateFailure ? t("reply_failed_no_position") : t("reply_failed"),
          variant: "error"
        });
      }).finally(() => {
        setSubmitting(false);
      });
    };
    $[27] = addReplyMutation;
    $[28] = onReplyAdded;
    $[29] = replyAccess;
    $[30] = replyText;
    $[31] = submitting;
    $[32] = t;
    $[33] = thesisSignature;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  const handleSubmitReply = t11;
  let t12;
  if ($[35] !== onReplyRemoved || $[36] !== t) {
    t12 = function handleDelete(replyId) {
      setDeletedIds(prev => new Set([...prev, replyId]));
      onReplyRemoved?.();
      showToast({
        id: TOAST_IDS.THESIS_REPLY_DELETED,
        message: t("reply_deleted"),
        variant: "success"
      });
    };
    $[35] = onReplyRemoved;
    $[36] = t;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  const handleDelete = t12;
  let t13;
  if ($[38] !== handleDelete || $[39] !== locale || $[40] !== tTime || $[41] !== userId || $[42] !== visibleReplies) {
    t13 = {
      replies: visibleReplies,
      userId,
      locale,
      tTime,
      onDelete: handleDelete
    };
    $[38] = handleDelete;
    $[39] = locale;
    $[40] = tTime;
    $[41] = userId;
    $[42] = visibleReplies;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  const replyListProps = t13;
  let t14;
  if ($[44] !== handleSubmitReply || $[45] !== isAuthenticated || $[46] !== replyText || $[47] !== submitting) {
    t14 = {
      isAuthenticated,
      replyText,
      onReplyTextChange: setReplyText,
      submitting,
      onSubmit: handleSubmitReply
    };
    $[44] = handleSubmitReply;
    $[45] = isAuthenticated;
    $[46] = replyText;
    $[47] = submitting;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  const replyInputProps = t14;
  const showSignInPrompt = writeAccess.status === "signed-out" && !isAuthLoading;
  let t15;
  if ($[49] !== openLogin || $[50] !== replyAccess || $[51] !== replyInputProps || $[52] !== showSignInPrompt || $[53] !== t) {
    t15 = showSignInPrompt ? <ReplyActionGate title={t("reply_sign_in_prompt")} description={t("reply_sign_in_description")} actionLabel={t("reply_sign_in_button")} onAction={openLogin} /> : replyAccess.canComposeReply ? <ReplyInput {...replyInputProps} /> : replyAccess.isChecking ? <p aria-live="polite" className="text-body text-foreground-secondary">{t("reply_access_checking")}</p> : replyAccess.isError ? <ReplyEligibilityUnavailable onRetry={replyAccess.retry} /> : replyAccess.isGateVisible ? <ReplyEligibilityGate canTrade={replyAccess.canTrade} accessRefreshPending={replyAccess.accessRefreshPending} minTradeUsdcD6={replyAccess.eligibility?.minTradeUsdcD6} onTradeNow={replyAccess.tradeNow} /> : null;
    $[49] = openLogin;
    $[50] = replyAccess;
    $[51] = replyInputProps;
    $[52] = showSignInPrompt;
    $[53] = t;
    $[54] = t15;
  } else {
    t15 = $[54];
  }
  const replyComposer = t15;
  if (isMobile) {
    let t16;
    if ($[55] !== onClose) {
      t16 = open => {
        if (!open) {
          onClose?.();
        }
      };
      $[55] = onClose;
      $[56] = t16;
    } else {
      t16 = $[56];
    }
    let t17;
    if ($[57] !== t) {
      t17 = t("comments_title");
      $[57] = t;
      $[58] = t17;
    } else {
      t17 = $[58];
    }
    let t18;
    if ($[59] !== replyListProps) {
      t18 = <ReplyList {...replyListProps} />;
      $[59] = replyListProps;
      $[60] = t18;
    } else {
      t18 = $[60];
    }
    let t19;
    if ($[61] !== replyComposer || $[62] !== t18) {
      t19 = <div className="flex flex-col gap-4">{t18}{replyComposer}</div>;
      $[61] = replyComposer;
      $[62] = t18;
      $[63] = t19;
    } else {
      t19 = $[63];
    }
    let t20;
    if ($[64] !== t16 || $[65] !== t17 || $[66] !== t19) {
      t20 = <SilkSheet open={true} onOpenChange={t16} variant="detached" size="lg" keyboard={true} title={t17}>{t19}</SilkSheet>;
      $[64] = t16;
      $[65] = t17;
      $[66] = t19;
      $[67] = t20;
    } else {
      t20 = $[67];
    }
    return t20;
  }
  let t16;
  if ($[68] !== replyListProps) {
    t16 = <ReplyList {...replyListProps} />;
    $[68] = replyListProps;
    $[69] = t16;
  } else {
    t16 = $[69];
  }
  let t17;
  if ($[70] !== replyComposer || $[71] !== t16) {
    t17 = <div className="flex flex-col gap-4">{t16}{replyComposer}</div>;
    $[70] = replyComposer;
    $[71] = t16;
    $[72] = t17;
  } else {
    t17 = $[72];
  }
  return t17;
}

// ---------------------------------------------------------------------------
// Reply list rendering
// ---------------------------------------------------------------------------

interface ReplyListProps {
  replies: ThesisReply[];
  userId: string | null;
  locale: string;
  tTime: (key: string, params?: Record<string, number>) => string;
  onDelete: (replyId: string) => void;
}
function ReplyList(t0) {
  const $ = _c(15);
  const {
    replies,
    userId,
    locale,
    tTime,
    onDelete
  } = t0;
  const t = useTranslations("social.debate");
  if (replies.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== locale || $[1] !== onDelete || $[2] !== replies || $[3] !== t || $[4] !== tTime || $[5] !== userId) {
    let t2;
    if ($[7] !== locale || $[8] !== onDelete || $[9] !== t || $[10] !== tTime || $[11] !== userId) {
      t2 = reply => {
        const isDeleted = reply.deletedAt !== null;
        const isOwnReply = userId !== null && reply.author.userId === userId;
        return <div key={reply.id} className="flex gap-3"><Link href={`/portfolio/${reply.author.userId ?? ""}`} prefetch={false} className="shrink-0"><UserAvatar userId={reply.author.userId} displayName={reply.author.displayName} avatarUrl={reply.author.avatarUrl} nsfw={reply.author.avatarNsfw ?? false} rasterise={reply.author.avatarPreferences?.rasterise ?? false} size="sm" /></Link><div className="flex min-w-0 flex-1 flex-col gap-y-2"><div className="flex items-center gap-2"><Link href={`/portfolio/${reply.author.userId ?? ""}`} prefetch={false} className="min-w-0 truncate hover:underline"><UserIdentity user={reply.author} size="sm" className="gap-2" nameClassName="text-foreground-secondary" handleClassName="text-foreground-tertiary" /></Link><span className="shrink-0 text-caption text-foreground-tertiary">·</span><span className="shrink-0 text-caption text-foreground-tertiary">{formatRelativeTime(reply.createdAt, tTime, {
                  locale
                })}</span></div><p className={cn("text-body leading-snug", isDeleted && "italic text-foreground-secondary")}>{isDeleted ? "[deleted]" : reply.content}</p>{!isDeleted && isOwnReply && <div className="flex items-center gap-2"><AnimatedButton variant="tertiary" size="sm" onClick={() => onDelete(reply.id)} aria-label={t("aria_delete_reply")} className="h-11 w-11 px-1 md:h-6 md:w-auto"><Icon icon={Delete02Icon} className="size-3" /></AnimatedButton></div>}</div></div>;
      };
      $[7] = locale;
      $[8] = onDelete;
      $[9] = t;
      $[10] = tTime;
      $[11] = userId;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    t1 = replies.map(t2);
    $[0] = locale;
    $[1] = onDelete;
    $[2] = replies;
    $[3] = t;
    $[4] = tTime;
    $[5] = userId;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[13] !== t1) {
    t2 = <div className="flex flex-col gap-3">{t1}</div>;
    $[13] = t1;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  return t2;
}

// ---------------------------------------------------------------------------
// Reply eligibility gate rendering
// ---------------------------------------------------------------------------

interface ReplyActionGateProps {
  title: string;
  description: string | null;
  actionLabel: string | null;
  onAction: (() => void) | null;
  actionTestId?: string;
}
interface ReplyEligibilityUnavailableProps {
  onRetry: () => void;
}
interface ReplyEligibilityGateProps {
  canTrade: boolean;
  accessRefreshPending: boolean;
  minTradeUsdcD6: string | undefined;
  onTradeNow: () => void;
}
function ReplyActionGate(t0) {
  const $ = _c(14);
  const {
    title,
    description,
    actionLabel,
    onAction,
    actionTestId
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <h3 className="text-heading-xs text-foreground-primary">{title}</h3>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== description) {
    t2 = description !== null ? <p className="text-body text-foreground-secondary">{description}</p> : null;
    $[2] = description;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex min-w-0 flex-1 flex-col gap-1">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== actionLabel || $[8] !== actionTestId || $[9] !== onAction) {
    t4 = actionLabel !== null && onAction !== null ? <AnimatedButton variant="secondary" size="xl" onClick={onAction} className="w-full shrink-0 md:w-auto" data-testid={actionTestId}>{actionLabel}</AnimatedButton> : null;
    $[7] = actionLabel;
    $[8] = actionTestId;
    $[9] = onAction;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = <Panel density="compact" aria-live="polite"><PanelContent className="flex flex-col items-stretch gap-3 md:flex-row md:items-center md:justify-between">{t3}{t4}</PanelContent></Panel>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}
function ReplyEligibilityUnavailable(t0) {
  const $ = _c(12);
  const {
    onRetry
  } = t0;
  const t = useTranslations("social.debate");
  let t1;
  if ($[0] !== t) {
    t1 = t("reply_access_unavailable");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <p className="text-body text-foreground-secondary">{t1}</p>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t) {
    t3 = t("reply_access_retry");
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== onRetry || $[7] !== t3) {
    t4 = <AnimatedButton variant="secondary" size="sm" onClick={onRetry} className="self-start">{t3}</AnimatedButton>;
    $[6] = onRetry;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t4) {
    t5 = <section aria-live="polite" className="flex flex-col gap-2 border-y border-border py-3">{t2}{t4}</section>;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function ReplyEligibilityGate(t0) {
  const $ = _c(20);
  const {
    canTrade,
    accessRefreshPending,
    minTradeUsdcD6,
    onTradeNow
  } = t0;
  const t = useTranslations("social.debate");
  const {
    formatD6
  } = useFormattedCurrency();
  let t1;
  if ($[0] !== formatD6 || $[1] !== minTradeUsdcD6 || $[2] !== t) {
    t1 = minTradeUsdcD6 === undefined ? t("reply_gate_requirement_fallback") : t("reply_gate_requirement", {
      amount: formatD6(minTradeUsdcD6)
    });
    $[0] = formatD6;
    $[1] = minTradeUsdcD6;
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const requirement = t1;
  let t2;
  if ($[4] !== canTrade || $[5] !== t) {
    t2 = canTrade ? t("reply_gate_active_title") : t("reply_gate_closed_title");
    $[4] = canTrade;
    $[5] = t;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== accessRefreshPending || $[8] !== canTrade || $[9] !== requirement || $[10] !== t) {
    t3 = canTrade ? accessRefreshPending ? t("reply_access_updating") : requirement : null;
    $[7] = accessRefreshPending;
    $[8] = canTrade;
    $[9] = requirement;
    $[10] = t;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== canTrade || $[13] !== t) {
    t4 = canTrade ? t("reply_gate_trade_now") : null;
    $[12] = canTrade;
    $[13] = t;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const t5 = canTrade ? onTradeNow : null;
  let t6;
  if ($[15] !== t2 || $[16] !== t3 || $[17] !== t4 || $[18] !== t5) {
    t6 = <ReplyActionGate title={t2} description={t3} actionLabel={t4} onAction={t5} actionTestId="reply-gate-trade-now" />;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
}

// ---------------------------------------------------------------------------
// Reply input rendering
// ---------------------------------------------------------------------------

interface ReplyInputProps {
  isAuthenticated: boolean;
  replyText: string;
  onReplyTextChange: (value: string) => void;
  submitting: boolean;
  onSubmit: () => void;
}
function ReplyInput(t0) {
  const $ = _c(30);
  const {
    isAuthenticated,
    replyText,
    onReplyTextChange,
    submitting,
    onSubmit
  } = t0;
  const t = useTranslations("social.debate");
  if (!isAuthenticated) {
    return null;
  }
  const length = replyText.length;
  const isOverLimit = length > MAX_REPLY_LENGTH;
  const showCounter = length >= REPLY_COUNTER_THRESHOLD;
  let t1;
  if ($[0] !== isOverLimit || $[1] !== replyText || $[2] !== submitting) {
    t1 = replyText.trim().length > 0 && !isOverLimit && !submitting;
    $[0] = isOverLimit;
    $[1] = replyText;
    $[2] = submitting;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const canSubmit = t1;
  let t2;
  if ($[4] !== onReplyTextChange) {
    t2 = e => {
      onReplyTextChange(e.target.value);
    };
    $[4] = onReplyTextChange;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t) {
    t3 = t("reply_placeholder");
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== replyText || $[9] !== t2 || $[10] !== t3) {
    t4 = <TextareaField size="sm" value={replyText} onChange={t2} placeholder={t3} className="flex-1 resize-none" />;
    $[8] = replyText;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const t5 = !canSubmit;
  let t6;
  if ($[12] !== t) {
    t6 = t("reply_button");
    $[12] = t;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== onSubmit || $[15] !== submitting || $[16] !== t5 || $[17] !== t6) {
    t7 = <AnimatedButton variant="secondary" size="sm" onClick={onSubmit} disabled={t5} loading={submitting} className="shrink-0">{t6}</AnimatedButton>;
    $[14] = onSubmit;
    $[15] = submitting;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t4 || $[20] !== t7) {
    t8 = <div className="flex items-center gap-3">{t4}{t7}</div>;
    $[19] = t4;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== isOverLimit || $[23] !== length || $[24] !== showCounter || $[25] !== t) {
    t9 = showCounter ? <div className="mt-2 flex justify-end"><span className="sr-only">{t("reply_char_count", {
          count: length
        })}</span><span aria-hidden={true} className={cn("inline-flex items-baseline gap-0 text-caption font-medium tabular-nums", isOverLimit ? "text-feedback-error-content" : "text-foreground-tertiary")}><AnimatedNumber value={length} format={formatReplyCharCount} duration={REPLY_CHAR_COUNT_ANIMATION_MS} className="text-inherit" /><span className="text-inherit">{`/${String(MAX_REPLY_LENGTH)}`}</span></span></div> : null;
    $[22] = isOverLimit;
    $[23] = length;
    $[24] = showCounter;
    $[25] = t;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t8 || $[28] !== t9) {
    t10 = <div className="flex flex-col">{t8}{t9}</div>;
    $[27] = t8;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  return t10;
}